import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { translate as t } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Modal, Button } from "./ui";
import { ShieldCheck, ChevronDown, ChevronRight, X, ExternalLink, UserCog, Clock, Trash2, History, Download } from "lucide-react";

const KEY_ROUTE = {
  recipients: "/app/recipients", alerts: "/app/alerts", activity: "/app/activity", sync_backup: "/app/sync",
  privacy: "/app/privacy", account: "/app/account", purchases: "/app/purchases",
  runners: "/app/runners", support_console: "/app/support", demo_codes: "/app/demo-codes",
  settings: "/app/settings", security: "/app/security", cluster: "/app/cluster",
  store_listings: "/app/listings", screenshots: "/app/screenshots", audiences: "/app/audiences",
  dabl: "/app/dabl", master_enabler: "/app/enabler",
  family_tree: "/app/family", benefits: "/app/benefits", currency: "/app/currency", holdings: "/app/holdings",
  fop_seo: "/app/fop-seo", reviews: "/app/reviews",
};

const TIER = {
  none: { ring: "border-black/10 dark:border-white/10", dot: "bg-black/20 dark:bg-white/20", flash: "" },
  green: { ring: "border-[#24A148]/60", dot: "bg-[#24A148]", flash: "animate-pulse" },
  yellow: { ring: "border-[#F1C21B]/70", dot: "bg-[#F1C21B]", flash: "animate-pulse" },
  red: { ring: "border-[#DA1E28]/70", dot: "bg-[#DA1E28]", flash: "animate-pulse" },
};

function sevDot(sev) {
  return sev === "critical" ? "bg-[#DA1E28]" : sev === "warning" ? "bg-[#F1C21B]" : "bg-[#0F62FE]";
}

// ---------------- Delegation modal (admin only) ----------------
function DelegationModal({ open, onClose, onSaved }) {
  const { lang } = useApp();
  const [cands, setCands] = useState([]);
  const [groups, setGroups] = useState([]);
  const [slots, setSlots] = useState([{}, {}, {}]);
  const [sort, setSort] = useState("delegable");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr(""); setMsg("");
    Promise.all([
      client.get(`/admin/delegation/candidates?sort=${sort}`),
      client.get("/admin/delegation"),
    ]).then(([c, d]) => {
      setCands(c.data.candidates || []);
      setGroups(c.data.groups || []);
      const cur = d.data.delegations || [];
      const next = [{}, {}, {}];
      cur.slice(0, 3).forEach((x, i) => {
        next[i] = { delegate_id: x.delegate_id, duration: x.duration || "24h", nav_keys: x.nav_keys || [], notify_sms: !!x.notify_sms };
      });
      setSlots(next);
    }).catch((e) => setErr(apiErr(e)));
  }, [open, sort]);

  const setSlot = (i, patch) => setSlots((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const toggleKey = (i, key) => setSlots((s) => s.map((x, idx) => {
    if (idx !== i) return x;
    const keys = new Set(x.nav_keys || []);
    keys.has(key) ? keys.delete(key) : keys.add(key);
    return { ...x, nav_keys: [...keys] };
  }));

  const chosenElsewhere = (i, id) => slots.some((s, idx) => idx !== i && s.delegate_id === id);

  const save = async () => {
    setErr(""); setMsg(""); setBusy(true);
    const delegates = slots
      .filter((s) => s.delegate_id && (s.nav_keys || []).length)
      .map((s) => ({ delegate_id: s.delegate_id, nav_keys: s.nav_keys, duration: s.duration || "24h", notify_sms: !!s.notify_sms }));
    try {
      await client.put("/admin/delegation", { delegates });
      setMsg("Delegation saved.");
      onSaved && onSaved();
      setTimeout(() => onClose(), 500);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const clearSlot = (i) => setSlot(i, { delegate_id: "", nav_keys: [], duration: "24h" });

  return (
    <Modal open={open} onClose={onClose} title="Delegate admin access" testid="delegation-modal" wide>
      <div className="space-y-4 text-sm">
        <p className="opacity-70">Grant up to three Runner/Support accounts temporary access to specific panels — for lunch breaks, trips, or vacations. Only you (Prime) can see this console. Delegates get read + write to exactly the sub-items you tick, and access ends automatically at the chosen time.</p>
        <div className="flex items-center gap-2 text-xs" data-testid="delegation-sort-row">
          <span className="opacity-60">Order candidates by:</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="delegation-sort"
            className="rounded-md border border-black/15 bg-transparent px-2 py-1 dark:border-white/15">
            <option value="delegable">Seniority (delegable level)</option>
            <option value="office">Office</option>
            <option value="">Default</option>
          </select>
        </div>
        {err && <div data-testid="delegation-err" className="rounded-md bg-[#DA1E28]/10 px-3 py-2 text-[#DA1E28]">{err}</div>}
        {msg && <div data-testid="delegation-msg" className="rounded-md bg-[#24A148]/10 px-3 py-2 text-[#24A148]">{msg}</div>}
        {slots.map((s, i) => (
          <div key={i} className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid={`delegate-slot-${i}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--primary)] text-xs font-bold text-white">{i + 1}</span>
              <select value={s.delegate_id || ""} onChange={(e) => setSlot(i, { delegate_id: e.target.value })}
                data-testid={`delegate-select-${i}`}
                className="min-w-[14rem] rounded-md border border-black/15 bg-transparent px-2 py-1.5 dark:border-white/15">
                <option value="">— choose delegate —</option>
                {cands.map((c) => (
                  <option key={c.id} value={c.id} disabled={chosenElsewhere(i, c.id)}>
                    {c.label} · {c.role}{c.delegable ? ` · L${c.delegable_level}` : ""}{c.role_name ? ` · ${c.role_name}` : ""}{c.office_names && c.office_names.length ? ` · ${c.office_names.filter(Boolean).join("/")}` : ""}{chosenElsewhere(i, c.id) ? " — already chosen" : ""}
                  </option>
                ))}
              </select>
              <select value={s.duration || "24h"} onChange={(e) => setSlot(i, { duration: e.target.value })}
                data-testid={`delegate-duration-${i}`}
                className="rounded-md border border-black/15 bg-transparent px-2 py-1.5 dark:border-white/15">
                <option value="24h">24 hours</option>
                <option value="72h">72 hours</option>
                <option value="flat">Flat — until withdrawn</option>
              </select>
              <label data-testid={`delegate-sms-${i}`} className="flex cursor-pointer items-center gap-1.5 rounded-md border border-black/15 px-2 py-1.5 text-xs dark:border-white/15">
                <input type="checkbox" checked={!!s.notify_sms} onChange={(e) => setSlot(i, { notify_sms: e.target.checked })} />
                Also text (SMS)
              </label>
              {s.delegate_id && (
                <button onClick={() => clearSlot(i)} data-testid={`delegate-clear-${i}`}
                  className="ms-auto flex items-center gap-1 rounded px-2 py-1 text-xs text-[#DA1E28] hover:bg-[#DA1E28]/10">
                  <X size={13} /> Clear
                </button>
              )}
            </div>
            {s.delegate_id && (
              <div className="grid gap-2 sm:grid-cols-2">
                {groups.filter((g) => g.keys.length).map((g) => (
                  <div key={g.k} className="rounded-md bg-black/[.03] p-2 dark:bg-white/[.04]">
                    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-70">
                      <span className="grid h-4 w-4 place-items-center rounded bg-black/10 text-[9px] font-bold dark:bg-white/15">{g.k}</span>
                      {g.name}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.keys.map((k) => {
                        const on = (s.nav_keys || []).includes(k);
                        return (
                          <label key={k} data-testid={`delegate-key-${i}-${k}`}
                            className={`flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${on ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>
                            <input type="checkbox" className="hidden" checked={on} onChange={() => toggleKey(i, k)} />
                            {t(k, lang)}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="delegation-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="delegation-save">{busy ? "Saving…" : "Save & close"}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Audit trail modal (admin only) ----------------
const ACTION_STYLE = {
  granted: "bg-[#24A148]/15 text-[#24A148]", updated: "bg-[#0F62FE]/15 text-[#0F62FE]",
  extended: "bg-[#0F62FE]/15 text-[#0F62FE]", ended: "bg-[#DA1E28]/15 text-[#DA1E28]",
  expired: "bg-[#F1C21B]/20 text-[#8a6d00] dark:text-[#F1C21B]",
};

function AuditModal({ open, onClose }) {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    client.get("/admin/delegation/audit").then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, [open]);

  const dl = async (path, filename) => {
    setBusy(true);
    try {
      const res = await client.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Delegation audit trail" testid="delegation-audit-modal" wide>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <p className="opacity-70">Every grant, update, extension, admin end and auto-expiry — a clean record of each hand-off.</p>
          <span className="ms-auto flex gap-2">
            <Button variant="ghost" onClick={() => dl("/admin/delegation/audit.csv", "delegation_audit.csv")} disabled={busy} data-testid="delegation-audit-download-csv">
              <Download size={14} className="me-1" /> CSV
            </Button>
            <Button variant="ghost" onClick={() => dl("/admin/delegation/audit.pdf", "delegation_audit.pdf")} disabled={busy} data-testid="delegation-audit-download-pdf">
              <Download size={14} className="me-1" /> PDF
            </Button>
          </span>
        </div>
        {events.length === 0 ? (
          <p className="py-6 text-center text-xs opacity-50" data-testid="audit-empty">No delegation activity yet.</p>
        ) : (
          <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-black/10 dark:border-white/10">
            <table className="w-full text-start text-xs">
              <thead className="sticky top-0 bg-black/[.04] text-[10px] uppercase tracking-wide opacity-60 dark:bg-white/[.06]">
                <tr><th className="p-2">When</th><th className="p-2">Action</th><th className="p-2">Delegate</th><th className="p-2">Panels</th><th className="p-2">Ends</th><th className="p-2">By</th></tr>
              </thead>
              <tbody data-testid="audit-rows">
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-black/5 dark:border-white/5">
                    <td className="p-2 tabular-nums opacity-70">{(e.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACTION_STYLE[e.action] || "bg-black/10"}`}>{e.action_label}</span></td>
                    <td className="p-2 font-medium">{e.delegate_label}<span className="opacity-50"> · {e.delegate_role}</span></td>
                    <td className="p-2 opacity-70">{(e.nav_keys || []).length}{e.notify_sms ? " · +SMS" : ""}</td>
                    <td className="p-2 tabular-nums opacity-70">{e.expires_at ? e.expires_at.slice(0, 16).replace("T", " ") : "flat"}</td>
                    <td className="p-2 opacity-70">{e.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------- Top alert console ----------------
export default function AdminConsole() {
  const { isAdmin, delegatedKeys = [], delegationExpiresAt, lang } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [delegOpen, setDelegOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [dels, setDels] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  const show = isAdmin || delegatedKeys.length > 0;

  const load = useCallback(() => {
    if (!show) return;
    client.get("/admin/alerts/by-group").then((r) => setData(r.data)).catch(() => {});
    if (isAdmin) client.get("/admin/delegation").then((r) => setDels(r.data.delegations || [])).catch(() => {});
  }, [show, isAdmin]);

  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(iv);
  }, []);

  const fmtRemaining = (iso) => {
    if (!iso) return "flat";
    const ms = new Date(iso).getTime() - nowTick;
    if (ms <= 0) return "ending…";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  };

  const extend = async (did, duration) => {
    try { await client.post(`/admin/delegation/${did}/extend`, { duration }); load(); } catch { /* noop */ }
  };
  const endNow = async (did) => {
    try { await client.delete(`/admin/delegation/${did}`); load(); } catch { /* noop */ }
  };

  useEffect(() => {
    if (!show) return;
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [show, load]);

  const dismiss = async (id) => {
    try { await client.post(`/admin/alerts/${id}/read`); load(); } catch { /* noop */ }
  };

  if (!show || !data) return null;
  const groups = data.groups || [];
  const active = groups.find((g) => g.k === openGroup);

  return (
    <div className="mb-5" data-testid="admin-alert-console">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider opacity-50">Alert console</span>
        <span className="rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold dark:bg-white/15" data-testid="console-total">
          {data.total} unread
        </span>
        {!isAdmin && delegationExpiresAt !== undefined && delegatedKeys.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] opacity-70" data-testid="delegate-access-chip">
            <Clock size={12} /> delegated access · {fmtRemaining(delegationExpiresAt)}
          </span>
        )}
        {isAdmin && (
          <div className="ms-auto flex items-center gap-2">
            <button onClick={() => setAuditOpen(true)} data-testid="open-audit-btn"
              className="flex items-center gap-1.5 rounded-md border border-black/15 px-2.5 py-1 text-xs font-medium lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
              <History size={14} /> History
            </button>
            <button onClick={() => setDelegOpen(true)} data-testid="open-delegation-btn"
              className="flex items-center gap-1.5 rounded-md border border-[var(--primary)]/40 px-2.5 py-1 text-xs font-medium text-[var(--primary)] lift hover:bg-[var(--primary)]/10">
              <UserCog size={14} /> Delegate access
            </button>
          </div>
        )}
      </div>
      {!isAdmin && delegatedKeys.length > 0 && (
        <div data-testid="delegate-banner" className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-3 py-2 text-xs">
          <ShieldCheck size={15} className="text-[var(--primary)]" />
          <span><b>Delegated admin access.</b> You can use: {delegatedKeys.map((k) => t(k, lang)).join(", ")}.</span>
          <span className="ms-auto flex items-center gap-1 font-medium text-[var(--primary)]"><Clock size={12} /> {fmtRemaining(delegationExpiresAt)}</span>
        </div>
      )}
      {isAdmin && dels.length > 0 && (
        <div className="mb-3 rounded-lg border border-black/10 bg-white/60 p-2 dark:border-white/10 dark:bg-white/[.04]" data-testid="active-delegations">
          <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider opacity-50">Active delegations</div>
          <ul className="space-y-1">
            {dels.map((d) => (
              <li key={d.delegate_id} data-testid={`delegation-row-${d.delegate_id}`}
                className="flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 hover:bg-black/5 dark:hover:bg-white/10">
                <UserCog size={14} className="opacity-60" />
                <span className="text-sm font-medium">{d.delegate_label}</span>
                <span className="rounded-full bg-black/10 px-1.5 py-0.5 text-[10px] dark:bg-white/15">{d.nav_keys.length} panel{d.nav_keys.length !== 1 ? "s" : ""}</span>
                {d.notify_sms && <span className="text-[10px] opacity-55">+SMS</span>}
                <span className="flex items-center gap-1 text-[11px] opacity-70"><Clock size={12} /> {d.expires_at ? fmtRemaining(d.expires_at) : "flat — until withdrawn"}</span>
                <span className="ms-auto flex items-center gap-1">
                  <select defaultValue="" onChange={(e) => { if (e.target.value) { extend(d.delegate_id, e.target.value); e.target.value = ""; } }}
                    data-testid={`delegation-extend-${d.delegate_id}`}
                    className="rounded-md border border-black/15 bg-transparent px-1.5 py-1 text-xs dark:border-white/15">
                    <option value="">Extend…</option>
                    <option value="24h">+24 hours</option>
                    <option value="72h">+72 hours</option>
                    <option value="flat">Flat</option>
                  </select>
                  <button onClick={() => endNow(d.delegate_id)} data-testid={`delegation-end-${d.delegate_id}`}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[#DA1E28] hover:bg-[#DA1E28]/10">
                    <X size={12} /> End now
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2" data-testid="console-placards">
        {groups.map((g) => {
          const tier = TIER[g.tier] || TIER.none;
          const opened = openGroup === g.k;
          return (
            <button key={g.k} onClick={() => setOpenGroup(opened ? null : g.k)}
              data-testid={`placard-${g.k}`}
              className={`flex items-center gap-2 rounded-lg border-2 bg-white/60 px-3 py-2 text-start lift dark:bg-white/[.04] ${tier.ring} ${g.count > 0 ? tier.flash : ""}`}>
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11px] font-bold text-white ${tier.dot}`}>{g.k}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{g.name}</span>
                <span className="block text-[11px] opacity-55">{g.count === 0 ? "All clear" : `${g.count} alert${g.count > 1 ? "s" : ""}`}</span>
              </span>
              {g.count > 0 && (
                <span data-testid={`placard-count-${g.k}`} className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold text-white ${tier.dot}`}>{g.count}</span>
              )}
              {opened ? <ChevronDown size={15} className="opacity-50" /> : <ChevronRight size={15} className="opacity-50" />}
            </button>
          );
        })}
      </div>
      {active && (
        <div className="mt-2 rounded-lg border border-black/10 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[.04]" data-testid={`placard-panel-${active.k}`}>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-semibold">{active.name}</span>
            {active.first_key && (
              <button onClick={() => nav(KEY_ROUTE[active.first_key] || "/app")}
                data-testid={`placard-open-${active.k}`}
                className="ms-auto flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
                <ExternalLink size={12} /> Open panel
              </button>
            )}
          </div>
          {active.items.length === 0 ? (
            <p className="py-2 text-xs opacity-50">No unread alerts in this group.</p>
          ) : (
            <ul className="space-y-1.5">
              {active.items.map((it) => (
                <li key={it.id} data-testid={`console-item-${it.id}`} className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-black/5 dark:hover:bg-white/10">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${sevDot(it.severity)}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{it.title}</span>
                    <span className="block text-[11px] opacity-55">{(it.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </span>
                  <button onClick={() => dismiss(it.id)} data-testid={`console-dismiss-${it.id}`}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs opacity-60 hover:bg-[#DA1E28]/10 hover:text-[#DA1E28] hover:opacity-100">
                    <Trash2 size={12} /> Dismiss
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <DelegationModal open={delegOpen} onClose={() => setDelegOpen(false)} onSaved={load} />
      <AuditModal open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}
